import { useQuery } from '@tanstack/react-query';
import { mcpCall } from '../lib/mcp';
import './Backfill.css';

// getIntegrationStatus returns an object (not a list), so this is a status page
// rather than a RecordsTable. Shared .records chrome is reused for the header/panel/states.
// Spreetail is the only integration left. Shopify and Amazon were removed on
// 2026-08-27 and get_integration_status no longer returns them — reading a field the
// tool does not send would render "not configured" for a channel that does not exist,
// which reads as a fault rather than as an absence.
interface IntegrationStatus {
  spreetail: { mode: string; apiConfigured: boolean };
}

function Badge({ ok, okLabel = 'Connected', offLabel = 'Not configured' }: { ok: boolean; okLabel?: string; offLabel?: string }) {
  return <span className={`records__tag records__tag--${ok ? 'ok' : 'warn'}`}>{ok ? okLabel : offLabel}</span>;
}

export function Backfill() {
  const query = useQuery({
    queryKey: ['mcp', 'getIntegrationStatus'],
    queryFn: () => mcpCall<IntegrationStatus>('getIntegrationStatus'),
  });

  return (
    <section className="records">
      <header className="records__head">
        <div className="records__head-text">
          <h1 className="records__title">Backfill / Sync</h1>
          <p className="records__intro">
            Connection status for each external channel the MCP integrates with. Spreetail is the
            only one: Shopify and Amazon direct selling were both cancelled, and Spreetail is now
            the exclusive Amazon seller, treated as a wholesale customer rather than a channel
            Cabana operates.
          </p>
        </div>
      </header>

      <div className="records__panel">
        {query.isPending && (
          <div className="records__state" role="status" aria-live="polite">
            <span className="records__spinner" aria-hidden="true" /> Loading…
          </div>
        )}

        {query.isError && (
          <div className="records__state records__state--error" role="alert">
            <strong>Couldn’t load integration status.</strong>
            <span className="records__state-detail">{(query.error as Error).message}</span>
            <button type="button" className="records__retry" onClick={() => query.refetch()}>
              Try again
            </button>
          </div>
        )}

        {query.isSuccess && (
          <div className="integrations">
            <div className="integration">
              <span className="integration__name">Spreetail</span>
              <Badge
                ok={query.data.spreetail.apiConfigured}
                okLabel="API connected"
                offLabel={query.data.spreetail.mode}
              />
              <span className="integration__hint">Inventory baseline — {query.data.spreetail.mode}.</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
